import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { FalloDePanel } from '@/components/Panel';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { type Metricas as Datos, useMetricas } from './api';

/**
 * Metricas — cuánta gente vio, reaccionó y confirmó.
 *
 * La ve el autor de la publicación y el staff. Es una consulta puntual, así que
 * diálogo.
 */
export function Metricas({
  postId, abierto, onCerrar,
}: {
  postId: number;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const consulta = useMetricas(postId, abierto);

  return (
    <Dialog
      abierto={abierto}
      onCambio={onCerrar}
      titulo="Métricas de alcance"
      ancho="max-w-md"
      pie={
        <Button variant="ghost" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      {consulta.isPending ? (
        <Skeleton className="h-32" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="las métricas de la publicación"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : consulta.data ? (
        <Cuerpo m={consulta.data} />
      ) : null}
    </Dialog>
  );
}

function Cuerpo({ m }: { m: Datos }) {
  const tasa = Math.max(0, Math.min(100, Math.round(m.read_rate ?? 0)));
  return (
    <div className="flex flex-col gap-3">
      {m.audience_label && (
        <p className="text-2 text-muted">
          <i className="bi bi-bullseye" aria-hidden="true" /> Audiencia:{' '}
          <strong className="text-ink-2">{m.audience_label}</strong>
          {m.requires_ack && <span className="text-warn-ink"> · requiere confirmación</span>}
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Cifra icono="bi-people-fill" valor={m.reach} etiqueta="Alcance" />
        <Cifra icono="bi-hand-thumbs-up-fill" valor={m.reactions} etiqueta="Reacciones" />
        <Cifra icono="bi-chat-dots-fill" valor={m.comments} etiqueta="Comentarios" />
        <Cifra icono="bi-eye-fill" valor={m.reads} etiqueta="Leídos" />
      </div>

      <div>
        <p className="mb-1 flex justify-between text-2 text-ink-2">
          <span>Tasa de lectura</span>
          <strong className="tabular-nums">{tasa}%</strong>
        </p>
        <BarraDeProgreso pct={tasa} etiqueta="Tasa de lectura de la publicación" />
      </div>

      <Pie m={m} />
    </div>
  );
}

function Cifra({ icono, valor, etiqueta }: { icono: string; valor: number; etiqueta: string }) {
  return (
    <div className="rounded-sm border border-line bg-sunken px-2 py-2.5 text-center">
      <p className="text-6 font-extrabold leading-none tabular-nums text-brand-ink">
        <i className={`bi ${icono} text-4`} aria-hidden="true" /> {valor}
      </p>
      <p className="mt-1 text-1 text-muted">{etiqueta}</p>
    </div>
  );
}

/**
 * El cierre del informe. Son tres casos excluyentes y uno de ellos —la audiencia
 * vacía— se colaba antes por la rama del éxito: con alcance 0 no hay pendientes,
 * así que el diálogo anunciaba «Todos confirmaron lectura» sobre un comunicado
 * que no recibió nadie.
 */
function Pie({ m }: { m: Datos }) {
  if (m.sin_destinatarios) {
    return (
      <p
        className="rounded-sm border border-warn/40 bg-warn/12 px-3 py-2.5 text-2 text-ink"
        role="alert"
      >
        <i className="bi bi-exclamation-triangle-fill mr-1 text-warn-ink" aria-hidden="true" />
        Esta publicación tiene audiencia configurada pero <strong>no llegó a nadie</strong>.
        Revisa la segmentación.
      </p>
    );
  }
  if (!m.requires_ack) return null;

  if (m.pending_count) {
    const muestra = (m.pending_sample ?? []).slice(0, 8);
    // El «…» se calcula contra lo que de verdad viajó, no contra el total de
    // pendientes: el servidor recorta la muestra a 50.
    const hayMas = m.pending_count > muestra.length;
    return (
      <p className="text-2 text-muted">
        <strong className="text-ink-2">{m.pending_count}</strong> sin leer:{' '}
        {muestra.join(', ')}
        {hayMas ? '…' : ''}
      </p>
    );
  }

  return (
    <p className="text-2 font-semibold text-ok-ink">
      <i className="bi bi-check-all" aria-hidden="true" /> Todos confirmaron lectura.
    </p>
  );
}
